import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Divider,
  Grid,
  Typography,
  Box,
  useTheme,
  Button,
  useMediaQuery,
} from "@mui/material";
import { Field, reduxForm, getFormSyncErrors, getFormValues } from "redux-form";
import {
  renderTextField,
  MultiLineTextField,
} from "components/FormFields/FieldTypes";
import {
  Required,
  DescriptionRequired,
  MaxDescriptionRequired,
  emailValidator,
} from "components/FormFields/validators";
import SecondaryCenterTemplate from "components/SecondaryCenterTemplate";
import { homeActions } from "actions";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const ContactUs = (props) => {
  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    formErrors,
    formValues,
    contactUs,
    contactUsInfo,
    initialize,
    organizationInfo,
  } = props;

  const onFormSubmit = () => {
    contactUs(formValues);
    setShowForm(false);
  };

  useEffect(() => {
    document.getElementById("appcontent").scroll(0, 0);
    initialize({});
  }, []);

  useEffect(() => {
    props.setValue(5);
    if (contactUsInfo && Object.keys(contactUsInfo).length) {
      setShowForm(false);
    }
  }, [contactUsInfo]);

  const [showForm, setShowForm] = useState(true);
  const [isFocus, setIsFocus] = useState(false);

  return (
    <SecondaryCenterTemplate pageTitle="Contact Us">
      <Helmet>
        <title>
          Contact Us |{" "}
          {organizationInfo?.app?.appCode === "CVD"
            ? "Cuverd® Partner"
            : "Cuverd®"}
        </title>
        <meta
          name="description"
          content="Contact us page for questions about Cuverd, the prescription medication discount and savings mobile app and 
          website."
        />
        <meta property="og:title" content="Contact Us | Cuverd®" />
        <meta
          property="og:description"
          content="Contact us page for questions about Cuverd, the prescription medication discount and savings mobile app and 
          website."
        />
        <meta
          property="og:image"
          content="assets/images/cuverd-logo-header.png"
        />
        <link
          rel="alternate"
          href="https://www.cuverd.com/contact-us"
          hrefLang="en-us"
        />
        <link rel="canonical" href="https://www.cuverd.com/contact-us" />
      </Helmet>
      <Grid
        container
        sx={{ paddingTop: matchesSm ? 0 : "40px", paddingBottom: "60px" }}
      >
        <Grid item xs={12} sm={8} sx={{ margin: "auto" }}>
          <Box>
            {showForm ? (
              <form>
                <Grid item container>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    p={4}
                    pt={0}
                    sx={{ textAlign: "center" }}
                  >
                    <Box mt={1} mb={2}>
                      <Typography variant="text4">
                        Provide your information and we will respond within{" "}
                        <br /> 2 business days.
                      </Typography>
                    </Box>
                    <Typography
                      variant="h2LatoMedium"
                      sx={{
                        color: theme.palette.common.textGrey2,
                        fontFamily: "Lato-Regular",
                      }}
                    >
                      All fields are required.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} py={4} pt={0}>
                    <Field
                      name="firstName"
                      isRequired
                      component={renderTextField}
                      label="First Name"
                      validate={Required}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} py={4} pt={0}>
                    <Field
                      name="lastName"
                      isRequired
                      component={renderTextField}
                      label="Last Name"
                      validate={Required}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} py={4} pt={0}>
                    <Field
                      name="email"
                      isRequired
                      component={renderTextField}
                      label="Email Address"
                      validate={[Required, emailValidator]}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} py={4} pt={0}>
                    <Field
                      name="notes"
                      isRequired
                      isFocussed={isFocus}
                      component={MultiLineTextField}
                      onFocus={() => {
                        setIsFocus(true);
                      }}
                      onBlur={() => {
                        setIsFocus(false);
                      }}
                      innerhtmlContent="Please provide a brief description of why <br/> you're reaching out"
                      label="Please provide a brief description of why you're reaching out"
                      validate={[DescriptionRequired, MaxDescriptionRequired]}
                      rows={9}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ margin: "auto", textAlign: "center" }}
                  >
                    <Box mt={4}>
                      <Button
                        variant="large"
                        type="button"
                        onClick={onFormSubmit}
                        sx={{ wordSpacing: 2, minWidth: "100%" }}
                        disabled={Object.keys(formErrors).length ? true : false}
                      >
                        SEND
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            ) : (
              <Grid item container sx={{ minHeight: 240 }}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box>
                    <Typography variant="text2">THANK YOU</Typography>
                  </Box>
                  <Typography
                    variant="text4"
                    sx={{
                      color: theme.palette.common.textGrey2,
                      fontFamily: "Lato-Regular",
                    }}
                  >
                    We will get back to you shortly.
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{ margin: "auto", textAlign: "center" }}
                >
                  <Box mt={4}>
                    <Button
                      variant="large"
                      onClick={() => {
                        initialize({});
                        setShowForm(true);
                      }}
                      sx={{ wordSpacing: 2, minWidth: "100%" }}
                    >
                      SEND ANOTHER MESSAGE
                    </Button>
                  </Box>
                  <Box mt={4}>
                    <Button
                      variant="largeOutline"
                      component={Link}
                      to="/SearchMedication"
                      onClick={() => setShowForm(true)}
                      sx={{ wordSpacing: 2, minWidth: "100%" }}
                    >
                      SEARCH FOR MEDICATION
                    </Button>
                  </Box>
                  <Box mt={4}>
                    <Button
                      variant="largeOutline"
                      component={Link}
                      to="/frequently-asked-questions"
                      onClick={() => setShowForm(true)}
                      sx={{ wordSpacing: 2, minWidth: "100%" }}
                    >
                      FAQs
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
            <Box my={3}>
              <Divider />
            </Box>
            <Box sx={{ textAlign: { xs: "left", md: "center" } }} my={2}>
              <Typography variant="text3">Contact Us Now</Typography>
            </Box>
            <Box sx={{ textAlign: { xs: "left", md: "left" } }}>
              <Typography variant="text4" mr={0.5}>
                Phone:
              </Typography>
              <Typography
                variant="text4"
                sx={{
                  color: theme.palette.common.greenThree,
                  textDecoration: "underline",
                }}
              >
                <a
                  href="tel:+855-428-8373"
                  style={{
                    color: theme.palette.common.greenThree,
                    textDecoration: "underline",
                  }}
                >
                  855-428-8373
                </a>
              </Typography>
            </Box>
            <Box my={0.4} sx={{ textAlign: { xs: "left", md: "left" } }}>
              <Typography variant="text4" mr={0.5}>
                Email:
              </Typography>
              <Typography variant="text4" sx={{}}>
                <a
                  href="mailto:support@cuverd.com"
                  style={{
                    color: theme.palette.common.greenThree,
                    textDecoration: "underline",
                  }}
                >
                  support@cuverd.com
                </a>
              </Typography>
            </Box>
            <Box sx={{ textAlign: { xs: "left", md: "left" } }}>
              <Typography variant="text4" mr={0.5}>
                Address:
              </Typography>
              <Typography
                variant="text4"
                sx={{ color: theme.palette.common.greyColorOne }}
              >
                12250 Tamiami Trail East, Suite 310, Naples, Florida 34113
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </SecondaryCenterTemplate>
  );
};

const mapStateToProps = (state) => {
  return {
    formErrors: getFormSyncErrors("CONTACTUS_FORM")(state) || {},
    formValues: getFormValues("CONTACTUS_FORM")(state),
    contactUsInfo: state.contactUs.data,
    organizationInfo: state.organizationInfo.data,
  };
};

const mapDispatchToProps = {
  contactUs: (params) => homeActions.contactUs(params),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "CONTACTUS_FORM",
  })(ContactUs)
);
